/**
 * Aplicación instalable (PWA): registra el service worker en producción, guarda el ofrecimiento «Instalar» del navegador
 * y avisa cuando el servidor ya tiene otra versión del sistema. El service worker (public/sw.js) no guarda datos ni nada
 * de /api: sin conexión no se vende (decisiones 2026-10-19).
 */
import { useSyncExternalStore } from 'react';

/** Lo que Chrome, Edge y Android disparan cuando la aplicación se puede instalar (no viene en los tipos del DOM). */
interface EventoInstalar extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface EstadoPwa {
  /** El navegador ofrece instalarla (Chrome, Edge, Android): «Instalar app» abre su ventana. */
  puedeInstalar: boolean;
  /** iPhone o iPad: Safari no ofrece la ventana, «Instalar app» explica cómo agregarla a la pantalla de inicio. */
  ios: boolean;
  /** Ya se abrió como aplicación instalada (ventana propia). */
  instalada: boolean;
  /** El servidor ya tiene otra versión del sistema: se aplica al recargar. */
  versionNueva: boolean;
}

let estado: EstadoPwa = { puedeInstalar: false, ios: false, instalada: false, versionNueva: false };
let eventoInstalar: EventoInstalar | null = null;
const oyentes = new Set<() => void>();
const suscribir = (o: () => void) => {
  oyentes.add(o);
  return () => {
    oyentes.delete(o);
  };
};
function cambiar(parcial: Partial<EstadoPwa>) {
  estado = { ...estado, ...parcial };
  for (const o of oyentes) o();
}

export function usePwa(): EstadoPwa {
  return useSyncExternalStore(suscribir, () => estado, () => estado);
}

/** Se ofrece «Instalar app» si no está instalada y el navegador la ofrece, o en iPhone/iPad (con las instrucciones). */
export function mostrarInstalar(e: EstadoPwa): boolean {
  return !e.instalada && (e.puedeInstalar || e.ios);
}

/** iPhone, iPad o iPod. El iPad nuevo dice ser una Mac: se reconoce porque tiene pantalla táctil. */
export function esIos(agente: string, plataforma: string, puntosTactiles: number): boolean {
  return /iphone|ipad|ipod/i.test(agente) || (plataforma === 'MacIntel' && puntosTactiles > 1);
}

function abiertaComoApp(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches === true || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** Los archivos de /assets que carga un index.html: si cambian, es otra versión del sistema. */
export function archivosDeLaVersion(html: string): string {
  return [...new Set([...html.matchAll(/(?:src|href)=["'](\/assets\/[^"']+)["']/g)].map((m) => m[1]!))].toSorted().join(' ');
}

/** Abre la ventana «Instalar» del navegador. Devuelve true si la persona aceptó. */
export async function instalarApp(): Promise<boolean> {
  const e = eventoInstalar;
  if (!e) return false;
  eventoInstalar = null;
  cambiar({ puedeInstalar: false });
  try {
    await e.prompt();
    const { outcome } = await e.userChoice;
    return outcome === 'accepted';
  } catch {
    // El navegador ya no lo permite (p. ej. se pidió dos veces): queda el ícono de instalar de la barra de direcciones
    return false;
  }
}

/**
 * Un service worker nuevo puede quedar «en espera» si el viejo estaba ocupado justo al instalarse (Chrome no lo vuelve a
 * intentar): se le recuerda que se active al terminar de instalarse, 3 segundos después y en cada revisión de versión
 * (public/sw.js, evento «message»).
 */
function activarEnEspera() {
  navigator.serviceWorker.getRegistration().then((r) => r?.waiting?.postMessage('activar'), () => undefined);
}

function vigilar(nuevo: ServiceWorker | null) {
  nuevo?.addEventListener('statechange', () => {
    if (nuevo.state !== 'installed') return;
    activarEnEspera();
    window.setTimeout(activarEnEspera, 3000);
  });
}

/**
 * Se llama una vez, antes de dibujar la aplicación (el navegador puede ofrecer instalarla en cuanto carga).
 * `version` cambia en cada build (vite.config.ts): el service worker la usa para el nombre de su caché.
 */
export function iniciarPwa(version: string) {
  const instalada = abiertaComoApp();
  cambiar({ instalada, ios: !instalada && esIos(navigator.userAgent, navigator.platform, navigator.maxTouchPoints ?? 0) });
  window.addEventListener('beforeinstallprompt', (e) => {
    // Sin la barra automática del navegador: se instala desde el menú del usuario
    e.preventDefault();
    eventoInstalar = e as EventoInstalar;
    cambiar({ puedeInstalar: true });
  });
  window.addEventListener('appinstalled', () => {
    eventoInstalar = null;
    cambiar({ puedeInstalar: false, instalada: true });
  });

  // El service worker solo en producción (en desarrollo, Vite sirve los archivos sin hash y cambian a cada rato)
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const propios = archivosDeLaVersion(document.head.innerHTML);
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(`/sw.js?v=${encodeURIComponent(version)}`, { scope: '/' })
      .then((reg) => {
        vigilar(reg.installing);
        reg.addEventListener('updatefound', () => vigilar(reg.installing));
        activarEnEspera();
      })
      .catch(() => undefined);
  });
  // Otra ventana cargó una versión nueva y su service worker tomó el control también de esta
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const url = navigator.serviceWorker.controller?.scriptURL;
    const suya = url ? new URL(url).searchParams.get('v') : null;
    if (suya && suya !== version) cambiar({ versionNueva: true });
  });
  // Una sola ventana abierta todo el día: cada 10 minutos y al volver a ella se mira el index del servidor
  const revisar = () => {
    activarEnEspera();
    if (estado.versionNueva || !propios) return;
    fetch('/', { cache: 'no-store', headers: { accept: 'text/html' } })
      .then((r) => (r.ok ? r.text() : null))
      .then((html) => {
        const delServidor = html ? archivosDeLaVersion(html) : '';
        if (delServidor && delServidor !== propios) cambiar({ versionNueva: true });
      })
      .catch(() => undefined);
  };
  window.setInterval(revisar, 10 * 60_000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') revisar();
  });
}
